import { createContext, isValidElement, useContext, useId } from 'react';
import type { ReactNode } from 'react';
import { formatSaDate } from '@claripick/shared';
import { cx } from './cx';

interface FieldContextValue {
  controlId: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Reads the wiring a Field set up. Controls call this so aria-describedby and
 * aria-invalid are attached automatically: hand-wiring them is what made the
 * existing forms announce nothing when validation failed.
 */
export function useFieldControl(explicitId?: string) {
  const ctx = useContext(FieldContext);
  const fallback = useId();
  if (!ctx) return { id: explicitId ?? fallback, 'aria-describedby': undefined, 'aria-invalid': undefined };
  return {
    id: explicitId ?? ctx.controlId,
    'aria-describedby': ctx.describedBy,
    'aria-invalid': ctx.invalid || undefined,
    required: ctx.required || undefined,
  };
}

export interface FieldProps {
  label: ReactNode;
  /** Guidance shown before the user gets it wrong. */
  hint?: ReactNode;
  /** When set, the control turns invalid and the message is announced. */
  error?: ReactNode;
  required?: boolean;
  /** Hide the label visually but keep it for assistive tech. */
  hideLabel?: boolean;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The chosen date, written the South African way, when the control is a
 * native date input. The native picker is the best date control a phone
 * has, but it prints the device's locale: a phone set to US English shows
 * 11/30/2026 for the 30th of November, and the treasurer reading it has to
 * work out which number is the month. The echo under it says 30 Nov 2026.
 */
function dateEcho(children: ReactNode): string | null {
  if (!isValidElement(children)) return null;
  const props = children.props as { type?: unknown; value?: unknown };
  if (props.type !== 'date' || typeof props.value !== 'string' || !ISO_DATE.test(props.value)) return null;
  return formatSaDate(props.value);
}

/** Label above, Instrument Sans 600 at 13.5px. Hint in ink-3. Error in danger. */
export function Field({ label, hint, error, required, hideLabel, htmlFor, className, children }: FieldProps) {
  const auto = useId();
  const controlId = htmlFor ?? `f-${auto}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const echo = dateEcho(children);

  return (
    <FieldContext.Provider value={{ controlId, describedBy, invalid: Boolean(error), required: Boolean(required) }}>
      <div className={cx('flex flex-col gap-1', className)}>
        <label
          htmlFor={controlId}
          className={cx('text-xs font-semibold text-ink-2', hideLabel && 'sr-only')}
        >
          {label}
          {required ? (
            <span className="text-danger" aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
        </label>
        {hint ? (
          <p id={hintId} className="text-xs text-ink-3">
            {hint}
          </p>
        ) : null}
        {children}
        {/* Visual only: the date input itself announces its value. */}
        {echo ? (
          <p aria-hidden="true" className="record text-xs text-ink-3">
            {echo}
          </p>
        ) : null}
        {error ? (
          // Polite, not assertive: a form with several errors should not
          // interrupt the user six times in a row.
          <p id={errorId} role="status" className="flex items-center gap-1 text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
